import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useDelete, useOne, useUpdate } from "@refinedev/core";
import { useNavigate, useParams } from "react-router-dom";
import authService from "../../../services/authService";
import api from "../../../services/api";
import { QrCodePanel } from "./QrCodePanel";

import type { Restaurant } from "../../types";

/** Shape returned by GET /restaurants/configurations */
interface QuerySetupConfiguration {
  id: string;
  name: string;
  description: string;
  modelName: string;
}

interface QuerySetupModel {
  value: string;
  label: string;
}

export function RestaurantEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  // Debug: log the ID and auth status to verify everything is set up correctly
  useEffect(() => {
    console.log("[RestaurantEdit] Route params id:", id);
    console.log("[RestaurantEdit] Auth token exists:", !!authService.getAuthToken());
    console.log("[RestaurantEdit] Is logged in:", authService.isLoggedIn());
    console.log("[RestaurantEdit] Is admin:", authService.isAdmin());
  }, [id]);

  const { data, isLoading, isError, error: fetchError } = useOne<Restaurant>({
    resource: "restaurants",
    id: id as string,
  });

  const { mutateAsync: updateAsync, isLoading: isSaving } = useUpdate<Restaurant>();
  const { mutateAsync: deleteAsync, isLoading: isDeleting } = useDelete();

  const restaurant = data?.data;

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [querySetup, setQuerySetup] = useState<{ modelName: string; configurationId: string }>({
    modelName: "",
    configurationId: "",
  });
  const [colorScheme, setColorScheme] = useState<'light' | 'dark' | null>('light');
  const [cartEnabled, setCartEnabled] = useState<boolean>(true);
  const [showScanSection, setShowScanSection] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Options for query setup dropdowns (fetched from API)
  const [configurations, setConfigurations] = useState<QuerySetupConfiguration[]>([]);
  const [models, setModels] = useState<QuerySetupModel[]>([]);

  /** Fetch available AI models and restaurant configurations for query setup dropdowns */
  const fetchConfigurations = async () => {
    try {
      const response = await api.get("/restaurants/configurations");
      setConfigurations(response.data.configurations ?? []);
      setModels(response.data.models ?? []);
    } catch (err) {
      console.error("Failed to load query setup options:", err);
    }
  };

  useEffect(() => {
    fetchConfigurations();
  }, []);

  useEffect(() => {
    if (!restaurant) return;

    setName(restaurant.name ?? "");
    setDescription(restaurant.description ?? "");
    setAdminNotes(restaurant.adminNotes ?? "");
    setColorScheme(restaurant.colorScheme ?? 'light');
    setCartEnabled(restaurant.cartEnabled !== false);
    setShowScanSection(restaurant.showScanSection !== false);

    // Populate query setup from restaurant; support both object and unknown (e.g. from API)
    const qs = restaurant.querySetup as { modelName?: string; configurationId?: string } | null | undefined;
    setQuerySetup({
      modelName: qs?.modelName ?? "",
      configurationId: qs?.configurationId ?? "",
    });
  }, [restaurant]);

  const handleSave = async () => {
    if (!restaurant) return;

    try {
      setError(null);

      // Only send querySetup if at least one field is set; otherwise send null to clear
      const payloadQuerySetup =
        querySetup.modelName || querySetup.configurationId
          ? {
              modelName: querySetup.modelName || undefined,
              configurationId: querySetup.configurationId || undefined,
            }
          : null;

      await updateAsync({
        resource: "restaurants",
        id: restaurant.id,
        values: {
          name: name.trim() || null,
          description: description.trim() || null,
          adminNotes: adminNotes.trim() || null,
          querySetup: payloadQuerySetup,
          colorScheme: colorScheme || 'light',
          cartEnabled,
          showScanSection,
        },
      });

      navigate(`/admin/restaurants/show/${restaurant.id}`);
    } catch (e: any) {
      setError(e?.message ?? "Failed to save restaurant");
    }
  };

  const handleDelete = async () => {
    if (!restaurant) return;

    const confirmed = window.confirm(
      `Delete restaurant "${restaurant.name ?? restaurant.id}"? This will also delete its menus/pages/items.`,
    );
    if (!confirmed) return;

    try {
      setError(null);
      await deleteAsync({ resource: "restaurants", id: restaurant.id });
      navigate("/admin/restaurants");
    } catch (e: any) {
      setError(e?.message ?? "Failed to delete restaurant");
    }
  };

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (isError) {
    // Check if it's an authentication error
    const isAuthError = fetchError?.message?.includes('401') || 
                       fetchError?.message?.includes('Authentication') ||
                       fetchError?.message?.includes('Unauthorized');
    
    return (
      <Stack gap="md">
        <Alert 
          color="red" 
          title={isAuthError ? "Authentication Error" : "Error loading restaurant"}
        >
          {isAuthError ? (
            <>
              <Text mb="sm">
                You need to be logged in as an admin to view this restaurant.
              </Text>
              <Text size="sm" c="dimmed">
                Error: {fetchError?.message ?? "Authentication required"}
              </Text>
            </>
          ) : (
            fetchError?.message ?? "Failed to load restaurant"
          )}
        </Alert>
        <Group>
          {isAuthError && (
            <Button onClick={() => navigate("/login")}>
              Go to Login
            </Button>
          )}
          <Button variant="default" onClick={() => navigate("/admin/restaurants")}>
            Back to Restaurants
          </Button>
        </Group>
      </Stack>
    );
  }

  if (!restaurant) {
    return (
      <Stack gap="md">
        <Alert color="yellow" title="Restaurant not found">
          Restaurant with ID "{id}" was not found.
        </Alert>
        <Button onClick={() => navigate("/admin/restaurants")}>
          Back to Restaurants
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Edit restaurant</Title>
        <Button color="red" variant="light" loading={isDeleting} onClick={handleDelete}>
          Delete
        </Button>
      </Group>

      {error ? <Alert color="red">{error}</Alert> : null}

      <TextInput
        label="Name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <Textarea
        label="Description"
        minRows={3}
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />

      <Textarea
        label="Admin notes"
        description="Included in menu chat context. These are instructions for the AI to follow when responding to users."
        minRows={4}
        value={adminNotes}
        onChange={(e) => setAdminNotes(e.currentTarget.value)}
      />

      <Select
        label="Color Scheme"
        description="Choose the color scheme for menus from this restaurant"
        value={colorScheme || 'light'}
        onChange={(value) => setColorScheme((value as 'light' | 'dark') || 'light')}
        data={[
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]}
      />

      <Switch
        label="Enable cart"
        description="When on, customers see the cart bar and Add to Order on menu items. When off, both are hidden."
        checked={cartEnabled}
        onChange={(e) => setCartEnabled(e.currentTarget.checked)}
      />

      <Switch
        label="Show 'Scan to View Menu' section"
        description="When on, the QR code section appears at the bottom of the menu. When off, it is hidden."
        checked={showScanSection}
        onChange={(e) => setShowScanSection(e.currentTarget.checked)}
      />

      {/* AI Query Configuration: model and restaurant-type prompts used for menu Q&A */}
      <Paper p="md" withBorder>
        <Title order={4} mb="xs">
          AI Query Configuration
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          Configure which AI model and restaurant-type prompts are used when customers ask questions about this restaurant's menus. Leave empty to use defaults.
        </Text>
        <Stack>
          <Select
            label="AI Model"
            placeholder="Select AI model (optional)"
            description="Gemini model used for menu queries"
            data={models.map((m) => ({ value: m.value, label: m.label }))}
            value={querySetup.modelName || null}
            onChange={(value) =>
              setQuerySetup((prev) => ({ ...prev, modelName: value ?? "" }))
            }
            clearable
          />
          <Select
            label="Restaurant Configuration"
            placeholder="Select restaurant type (optional)"
            description="Predefined prompts (e.g. Japan Restaurant, American Restaurant)"
            data={configurations.map((c) => ({
              value: c.id,
              label: c.name,
              description: c.description,
            }))}
            value={querySetup.configurationId || null}
            onChange={(value) =>
              setQuerySetup((prev) => ({ ...prev, configurationId: value ?? "" }))
            }
            clearable
          />
        </Stack>
      </Paper>

      {/* Standalone QR codes: permanently tied to this restaurant, repointable across menus */}
      <QrCodePanel restaurantId={restaurant.id} />

      <Group justify="flex-end">
        <Button variant="default" onClick={() => navigate(`/admin/restaurants/show/${restaurant.id}`)}>
          Cancel
        </Button>
        <Button loading={isSaving} onClick={handleSave}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
